import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ContinuityQualificationPanel } from './ContinuityQualificationPanel';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Database,
  RefreshCw,
  Search,
  ShieldAlert,
} from 'lucide-react';

import { useI18n } from '@/i18n';
import {
  continuityObservabilityApi,
  type ContinuityBaseline,
  type ContinuityFilters,
  type ContinuityRunDetail,
  type ReflectionCommit,
} from '@/lib/api/continuityObservability';
import type { HttpError } from '@/lib/api/client';


const COMMIT_STATUSES = ['pending', 'processing', 'committed', 'failed'] as const;
const ADOPTION_STATUSES = ['not_applicable', 'complete', 'missing', 'invalid'] as const;

type Filters = ContinuityFilters;

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(seconds: number): string {
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function statusClass(value: string): string {
  switch (value) {
    case 'committed':
    case 'complete':
    case 'projected':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800';
    case 'failed':
    case 'invalid':
    case 'dead_letter':
      return 'border-red-200 bg-red-50 text-red-800';
    case 'missing':
    case 'processing':
      return 'border-amber-200 bg-amber-50 text-amber-800';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-700';
  }
}

function StatusBadge({ value }: { value: string }) {
  return (
    <span className={`inline-flex max-w-full items-center rounded-md border px-2 py-0.5 text-xs font-medium ${statusClass(value)}`}>
      <span className="truncate">{value.replace(/_/g, ' ')}</span>
    </span>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-slate-950 px-4 py-3 text-xs leading-5 text-slate-100">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function IdentifierList({ values, empty }: { values: string[]; empty: string }) {
  if (!values.length) return <p className="text-xs text-slate-500">{empty}</p>;
  return (
    <ul className="space-y-1.5">
      {values.map((value) => (
        <li key={value} className="rounded-md bg-slate-100 px-2.5 py-1.5">
          <code className="block break-all text-[11px] text-slate-700">{value}</code>
        </li>
      ))}
    </ul>
  );
}

function DetailSection({ title, count, children, open = false }: { title: string; count?: number; children: ReactNode; open?: boolean }) {
  return (
    <details className="border-b border-slate-200 py-4 last:border-b-0" open={open}>
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2">
        <span>{title}</span>
        {count !== undefined && <span className="text-xs font-normal text-slate-500">{count}</span>}
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

function ErrorState({ error, retry, messages }: { error: Error; retry: () => void; messages: ReturnType<typeof useI18n>['messages']['continuityObservability'] }) {
  const denied = (error as HttpError).statusCode === 403;
  return (
    <div className="flex min-h-64 items-center justify-center px-8 py-12 text-center" role="alert">
      <div className="max-w-md">
        <ShieldAlert className="mx-auto h-9 w-9 text-red-600" aria-hidden="true" />
        <h2 className="mt-4 text-base font-semibold text-slate-900">
          {denied ? messages.permissionDenied : messages.loadFailed}
        </h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          {denied ? messages.permissionHint : error.message}
        </p>
        <button
          type="button"
          onClick={retry}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-medium text-white hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          {messages.retry}
        </button>
      </div>
    </div>
  );
}

function BaselineStrip({ baseline, messages }: { baseline: ContinuityBaseline; messages: ReturnType<typeof useI18n>['messages']['continuityObservability'] }) {
  const metrics = [
    [messages.failedRate, formatPercent(baseline.reflection_failed_rate)],
    [messages.missingRate, formatPercent(baseline.reflection_missing_rate)],
    [messages.adoptionValidRate, formatPercent(baseline.adoption_valid_rate)],
    [messages.pendingAge, formatDuration(baseline.oldest_pending_age_seconds)],
    [messages.commitDuration, formatDuration(baseline.average_commit_duration_seconds)],
    [messages.projectionLag, formatDuration(baseline.average_projection_lag_seconds)],
  ];
  const isolationClear = baseline.cross_user_isolation_violations === 0;

  return (
    <section className="border-y border-slate-200 bg-white" aria-labelledby="continuity-baseline-heading">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 id="continuity-baseline-heading" className="text-sm font-semibold text-slate-900">{messages.baseline}</h2>
            <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">{messages.trendOnly}</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {messages.window}: {formatDateTime(baseline.window_started_at)} – {formatDateTime(baseline.window_ended_at)} · {messages.sample}: {baseline.reflection_sample_size}
          </p>
          <p className="mt-1 text-xs text-slate-500">{messages.baselineScope}</p>
        </div>
        <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${isolationClear ? 'bg-slate-50 text-slate-700' : 'bg-red-50 text-red-800'}`}>
          {isolationClear ? <Database className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          {messages.isolationViolations}: {baseline.cross_user_isolation_violations}
        </div>
      </div>
      <dl className="grid grid-cols-2 border-t border-slate-100 md:grid-cols-3 xl:grid-cols-6">
        {metrics.map(([label, value]) => (
          <div key={label} className="border-b border-r border-slate-100 px-4 py-3 last:border-r-0 md:border-b-0">
            <dt className="text-xs text-slate-500">{label}</dt>
            <dd className="mt-1 text-sm font-semibold tabular-nums text-slate-900">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function RunTable({ rows, selected, onSelect, messages }: { rows: ReflectionCommit[]; selected: ReflectionCommit | null; onSelect: (row: ReflectionCommit) => void; messages: ReturnType<typeof useI18n>['messages']['continuityObservability'] }) {
  return (
    <div className="h-full overflow-auto">
      <table className="w-full min-w-[600px] text-left text-sm">
        <thead className="sticky top-0 z-10 bg-slate-50 text-xs font-medium text-slate-500">
          <tr>
            <th className="px-4 py-3">{messages.run}</th>
            <th className="px-4 py-3">{messages.user}</th>
            <th className="px-4 py-3">{messages.status}</th>
            <th className="px-4 py-3">{messages.adoption}</th>
            <th className="px-4 py-3">{messages.updated}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row) => {
            const active = selected?.commit_id === row.commit_id;
            return (
              <tr
                key={row.commit_id}
                className={active ? 'bg-blue-50/70' : 'bg-white hover:bg-slate-50'}
              >
                <td className="max-w-52 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => onSelect(row)}
                    className="block w-full truncate text-left font-mono text-xs font-medium text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    title={row.run_id}
                  >
                    {row.run_id}
                  </button>
                  <span className="mt-1 block text-xs text-slate-500">{row.agent_type}</span>
                </td>
                <td className="max-w-44 truncate px-4 py-3 font-mono text-xs text-slate-600" title={row.user_id}>{row.user_id}</td>
                <td className="px-4 py-3"><StatusBadge value={row.status} /></td>
                <td className="px-4 py-3"><StatusBadge value={row.adoption_status} /></td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{formatDateTime(row.updated_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EvidenceList<T>({ rows, empty, render }: { rows: T[]; empty: string; render: (row: T, index: number) => ReactNode }) {
  if (!rows.length) return <p className="text-sm text-slate-500">{empty}</p>;
  return <div className="space-y-3">{rows.map((row, index) => render(row, index))}</div>;
}

export function ContinuityRunPanel({ detail, messages }: { detail: ContinuityRunDetail; messages: ReturnType<typeof useI18n>['messages']['continuityObservability'] }) {
  const reflection = detail.reflection;
  const runId = reflection?.run_id ?? detail.experiences[0]?.run_id;
  return (
    <div className="h-full overflow-y-auto px-5 pb-10">
      <div className="sticky top-0 z-10 -mx-5 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="truncate font-mono text-xs text-slate-500" title={runId}>{runId}</p>
            {reflection ? <div className="mt-2 flex flex-wrap gap-2">
              <StatusBadge value={reflection.status} />
              <StatusBadge value={reflection.adoption_status} />
              <span className="text-xs text-slate-500">{messages.attempt} {reflection.attempt}</span>
            </div> : <p className="mt-2 text-xs text-slate-500">{messages.noReflection}</p>}
          </div>
          <div className={`flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium ${detail.provenance_complete ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
            {detail.provenance_complete ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {detail.provenance_complete ? messages.completeProvenance : messages.partialProvenance}
          </div>
        </div>
      </div>

      {reflection && (reflection.error_detail || reflection.adoption_error) && (
        <div className="mt-5 rounded-xl bg-red-50 p-4 text-red-900">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4" /> {messages.failure}
          </div>
          <div className="mt-3"><JsonBlock value={{ error_code: reflection.error_code, error_detail: reflection.error_detail, adoption_error: reflection.adoption_error }} /></div>
        </div>
      )}

      <div className="mt-2">
        {reflection && <DetailSection title={messages.ledger} open>
          <dl className="grid grid-cols-1 gap-3 text-xs sm:grid-cols-2">
            <div><dt className="text-slate-500">{messages.parentRun}</dt><dd className="mt-1 break-all font-mono text-slate-900">{reflection.parent_run_id || '—'}</dd></div>
            <div><dt className="text-slate-500">{messages.inputDigest}</dt><dd className="mt-1 break-all font-mono text-slate-900">{reflection.input_digest}</dd></div>
            <div><dt className="text-slate-500">{messages.created}</dt><dd className="mt-1 text-slate-900">{formatDateTime(reflection.created_at)}</dd></div>
            <div><dt className="text-slate-500">{messages.updated}</dt><dd className="mt-1 text-slate-900">{formatDateTime(reflection.updated_at)}</dd></div>
          </dl>
          <div className="mt-4">
            <h4 className="mb-2 text-xs font-semibold text-slate-600">{messages.branches}</h4>
            <IdentifierList values={reflection.adopted_branch_ids} empty={messages.noData} />
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div><h4 className="mb-2 text-xs font-semibold text-slate-600">{messages.modelProvenance}</h4><JsonBlock value={reflection.model_provenance} /></div>
            <div><h4 className="mb-2 text-xs font-semibold text-slate-600">{messages.configProvenance}</h4><JsonBlock value={reflection.config_provenance} /></div>
          </div>
        </DetailSection>}

        <DetailSection title={messages.workspace} open>
          {detail.workspace ? (
            <div className="space-y-4">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div><dt className="text-slate-500">{messages.revision}</dt><dd className="mt-0.5 font-medium text-slate-900">{detail.workspace.self_contract_revision}</dd></div>
                <div><dt className="text-slate-500">{messages.itemsTokens}</dt><dd className="mt-0.5 font-medium text-slate-900">{detail.workspace.item_count} / {detail.workspace.token_count}</dd></div>
              </dl>
              <div><h4 className="mb-2 text-xs font-semibold text-slate-600">{messages.controlVars}</h4><JsonBlock value={detail.workspace.control_vars} /></div>
              <div><h4 className="mb-2 text-xs font-semibold text-slate-600">{messages.attendedState}</h4><JsonBlock value={detail.workspace.attended_state} /></div>
            </div>
          ) : <p className="text-sm text-slate-500">{messages.noData}</p>}
        </DetailSection>

        <DetailSection title={messages.timeline} count={detail.workspace_timeline.length}>
          <EvidenceList rows={detail.workspace_timeline} empty={messages.noData} render={(row) => (
            <div key={row.snapshot_id} className="rounded-lg bg-slate-50 p-3">
              <div className="flex items-center justify-between gap-3 text-xs"><code className="truncate text-slate-700">{row.run_id}</code><span className="shrink-0 text-slate-500">{formatDateTime(row.created_at)}</span></div>
              <div className="mt-2"><JsonBlock value={row.control_vars} /></div>
            </div>
          )} />
        </DetailSection>

        <DetailSection title={messages.experiences} count={detail.experiences.length}>
          <EvidenceList rows={detail.experiences} empty={messages.noData} render={(row) => (
            <div key={row.event_id} className="rounded-lg bg-slate-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2"><StatusBadge value={row.event_type} /><span className="text-xs text-slate-500">{formatDateTime(row.occurred_at_utc)}</span></div>
              <p className="mt-2 break-all font-mono text-[11px] text-slate-500">{row.event_id}</p>
              <div className="mt-2"><JsonBlock value={{ evidence_refs: row.evidence_refs, payload: row.payload }} /></div>
            </div>
          )} />
        </DetailSection>

        <DetailSection title={messages.beliefs} count={detail.beliefs.length}>
          <EvidenceList rows={detail.beliefs} empty={messages.noData} render={(row) => (
            <div key={row.belief_revision_id} className="rounded-lg bg-slate-50 p-3">
              <div className="flex items-start justify-between gap-3"><p className="text-sm leading-6 text-slate-800">{row.statement}</p><StatusBadge value={row.status} /></div>
              <p className="mt-2 text-xs text-slate-500">{messages.confidence} {row.confidence.toFixed(2)}</p>
              <div className="mt-2"><h4 className="mb-1 text-xs font-medium text-slate-600">{messages.evidenceIds}</h4><IdentifierList values={row.evidence_event_ids} empty={messages.noData} /></div>
            </div>
          )} />
        </DetailSection>

        <DetailSection title={messages.commitments} count={detail.commitments.length}>
          <EvidenceList rows={detail.commitments} empty={messages.noData} render={(row) => (
            <div key={row.commitment_revision_id} className="rounded-lg bg-slate-50 p-3">
              <div className="flex items-start justify-between gap-3"><p className="text-sm leading-6 text-slate-800">{row.commitment}</p><StatusBadge value={row.status} /></div>
              <p className="mt-2 text-xs font-medium text-slate-600">{messages.authorization}</p>
              <p className="mt-1 break-all font-mono text-[11px] text-slate-500">{row.authorization_ref}</p>
              <div className="mt-2"><h4 className="mb-1 text-xs font-medium text-slate-600">{messages.fulfillmentEvidence}</h4><IdentifierList values={row.fulfillment_event_ids} empty={messages.noData} /></div>
            </div>
          )} />
        </DetailSection>

        <DetailSection title={messages.relationships} count={detail.relationships.length}>
          <EvidenceList rows={detail.relationships} empty={messages.noData} render={(row) => (
            <div key={row.revision_id} className="rounded-lg bg-slate-50 p-3"><JsonBlock value={{ state: row.state, memory_projection: row.memory_projection, evidence: row.derived_from_event_ids }} /></div>
          )} />
        </DetailSection>

        <DetailSection title={messages.projections} count={detail.memory_projections.length}>
          <EvidenceList rows={detail.memory_projections} empty={messages.noData} render={(row) => (
            <div key={row.projection_id} className="rounded-lg bg-slate-50 p-3">
              <div className="flex items-center justify-between gap-3"><StatusBadge value={row.status} /><span className="text-xs text-slate-500">{formatDateTime(row.updated_at)}</span></div>
              <p className="mt-2 break-all font-mono text-[11px] text-slate-500">{row.source_event_id}</p>
              {(row.error_detail || row.source_provenance) && <div className="mt-2"><JsonBlock value={{ source_provenance: row.source_provenance, error_code: row.error_code, error_detail: row.error_detail }} /></div>}
            </div>
          )} />
        </DetailSection>

        <DetailSection title={messages.provenance} count={detail.provenance_events.length + detail.missing_provenance_event_ids.length}>
          {detail.missing_provenance_event_ids.length > 0 && (
            <div className="mb-3 rounded-lg bg-amber-50 p-3 text-xs leading-5 text-amber-900">
              {messages.partialProvenance}: {detail.missing_provenance_event_ids.join(', ')}
            </div>
          )}
          <EvidenceList rows={detail.provenance_events} empty={messages.noData} render={(row) => (
            <div key={row.event_id} className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 p-3 text-xs"><code className="truncate text-slate-700">{row.event_id}</code><StatusBadge value={row.event_type} /></div>
          )} />
        </DetailSection>
      </div>
    </div>
  );
}

export function ContinuityObservabilityPage() {
  const { messages } = useI18n();
  const t = messages.continuityObservability;
  const [draft, setDraft] = useState<Filters>({ tenantId: '' });
  const [applied, setApplied] = useState<Filters>({ tenantId: '' });
  const [selected, setSelected] = useState<ReflectionCommit | null>(null);

  const identity = useQuery({
    queryKey: ['admin-tenant'],
    queryFn: continuityObservabilityApi.getAdminTenant,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!identity.data) return;
    setDraft((current) => ({ ...current, tenantId: identity.data }));
    setApplied((current) => current.tenantId ? current : { ...current, tenantId: identity.data });
  }, [identity.data]);

  const runs = useQuery({
    queryKey: ['continuity-runs', applied],
    queryFn: () => continuityObservabilityApi.listRuns(applied),
    enabled: Boolean(applied.tenantId),
  });
  const baseline = useQuery({
    queryKey: ['continuity-baseline', applied.tenantId, applied.agentType, applied.userId],
    queryFn: () => continuityObservabilityApi.getBaseline({
      tenantId: applied.tenantId,
      agentType: applied.agentType,
      userId: applied.userId,
    }),
    enabled: Boolean(applied.tenantId),
  });
  const detailFilters = useMemo(() => selected ? {
    tenantId: selected.tenant_id,
    agentType: selected.agent_type,
    userId: selected.user_id,
    runId: selected.run_id,
  } : applied.tenantId && applied.agentType && applied.userId && applied.runId ? {
    tenantId: applied.tenantId,
    agentType: applied.agentType,
    userId: applied.userId,
    runId: applied.runId,
  } : null, [selected, applied]);
  const detail = useQuery({
    queryKey: ['continuity-run', detailFilters],
    queryFn: () => continuityObservabilityApi.getRun(detailFilters!),
    enabled: Boolean(detailFilters),
  });

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setSelected(null);
    setApplied({
      tenantId: draft.tenantId.trim(),
      agentType: draft.agentType?.trim() || undefined,
      userId: draft.userId?.trim() || undefined,
      runId: draft.runId?.trim() || undefined,
      status: draft.status || undefined,
      adoptionStatus: draft.adoptionStatus || undefined,
    });
  };

  const refresh = () => {
    void runs.refetch();
    void baseline.refetch();
    if (detailFilters) void detail.refetch();
  };

  const primaryError = identity.error || runs.error || baseline.error;
  const loading = identity.isLoading || (Boolean(applied.tenantId) && (runs.isLoading || baseline.isLoading));

  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto bg-slate-50">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-white px-6 py-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-slate-950">{t.pageTitle}</h1>
            <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
              <Database className="h-3.5 w-3.5" aria-hidden="true" /> {t.readOnly}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-600">{t.pageDescription}</p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /> {t.refresh}
        </button>
      </header>

      <form onSubmit={applyFilters} className="border-b border-slate-200 bg-white px-6 py-4" aria-label={t.filters}>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-7">
          <label className="xl:col-span-1"><span className="mb-1 block text-xs font-medium text-slate-600">{t.tenant}</span><input aria-label={t.tenant} value={draft.tenantId} readOnly className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-600 outline-none" /></label>
          <label><span className="mb-1 block text-xs font-medium text-slate-600">{t.agent}</span><input aria-label={t.agent} value={draft.agentType || ''} onChange={(event) => setDraft({ ...draft, agentType: event.target.value })} placeholder="soulmate" className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" /></label>
          <label><span className="mb-1 block text-xs font-medium text-slate-600">{t.user}</span><input aria-label={t.user} value={draft.userId || ''} onChange={(event) => setDraft({ ...draft, userId: event.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" /></label>
          <label><span className="mb-1 block text-xs font-medium text-slate-600">{t.run}</span><input aria-label={t.run} value={draft.runId || ''} onChange={(event) => setDraft({ ...draft, runId: event.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" /></label>
          <label><span className="mb-1 block text-xs font-medium text-slate-600">{t.status}</span><select aria-label={t.status} value={draft.status || ''} onChange={(event) => setDraft({ ...draft, status: event.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"><option value="">{t.all}</option>{COMMIT_STATUSES.map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
          <label><span className="mb-1 block text-xs font-medium text-slate-600">{t.adoption}</span><select aria-label={t.adoption} value={draft.adoptionStatus || ''} onChange={(event) => setDraft({ ...draft, adoptionStatus: event.target.value })} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"><option value="">{t.all}</option>{ADOPTION_STATUSES.map((value) => <option key={value} value={value}>{value.replace(/_/g, ' ')}</option>)}</select></label>
          <button type="submit" disabled={!draft.tenantId || loading} className="mt-5 inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 text-sm font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"><Search className="h-4 w-4" aria-hidden="true" />{t.apply}</button>
        </div>
      </form>

      {primaryError ? (
        <ErrorState error={primaryError as Error} retry={refresh} messages={t} />
      ) : loading ? (
        <div className="flex flex-1 flex-col" aria-label={t.loading} aria-busy="true">
          <div className="h-28 animate-pulse border-b border-slate-200 bg-white" />
          <div className="grid flex-1 grid-cols-5 gap-px bg-slate-200"><div className="col-span-3 animate-pulse bg-white" /><div className="col-span-2 animate-pulse bg-slate-50" /></div>
        </div>
      ) : (
        <>
          {baseline.data && <BaselineStrip baseline={baseline.data} messages={t} />}
          <ContinuityQualificationPanel tenantId={applied.tenantId} agentType={applied.agentType} userId={applied.userId} />
          <main className="grid min-h-[420px] flex-1 grid-cols-1 gap-px bg-slate-200 xl:grid-cols-5">
            <section className="col-span-1 flex min-h-[360px] min-w-0 flex-col bg-white xl:col-span-3" aria-labelledby="continuity-runs-heading">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <h2 id="continuity-runs-heading" className="text-sm font-semibold text-slate-900">{t.runs}</h2>
                <span className="text-xs text-slate-500">{t.runCount.replace('{count}', String(runs.data?.total || 0))}</span>
              </div>
              {!runs.data?.items.length ? (
                <div className="flex flex-1 items-center justify-center px-8 py-12 text-center">
                  <div className="max-w-sm"><Clock3 className="mx-auto h-9 w-9 text-slate-400" /><h3 className="mt-4 text-sm font-semibold text-slate-900">{t.empty}</h3><p className="mt-2 text-sm leading-6 text-slate-500">{t.emptyHint}</p></div>
                </div>
              ) : <RunTable rows={runs.data.items} selected={selected} onSelect={setSelected} messages={t} />}
            </section>
            <aside className="col-span-1 min-h-[420px] min-w-0 bg-white xl:col-span-2" aria-labelledby="continuity-detail-heading">
              <div className="border-b border-slate-200 px-5 py-3"><h2 id="continuity-detail-heading" className="text-sm font-semibold text-slate-900">{t.details}</h2></div>
              {!detailFilters ? (
                <div className="flex h-[calc(100%-49px)] items-center justify-center px-8 py-12 text-center"><div className="max-w-xs"><Database className="mx-auto h-9 w-9 text-slate-400" /><p className="mt-4 text-sm leading-6 text-slate-500">{t.selectRun}</p></div></div>
              ) : detail.error ? (
                <ErrorState error={detail.error as Error} retry={() => void detail.refetch()} messages={t} />
              ) : detail.isLoading || !detail.data ? (
                <div className="space-y-4 p-5" aria-busy="true"><div className="h-16 animate-pulse rounded-lg bg-slate-100" /><div className="h-40 animate-pulse rounded-lg bg-slate-100" /><div className="h-24 animate-pulse rounded-lg bg-slate-100" /></div>
              ) : <ContinuityRunPanel detail={detail.data} messages={t} />}
            </aside>
          </main>
        </>
      )}
    </div>
  );
}
